import type {
  CustomAnalyticsChart,
  CustomAnalyticsChartSeries,
  CustomAnalyticsChartTruncation,
  CustomAnalyticsChartValueFormat,
} from "@redotech/redo-analytics-common/reports/custom-analytics-chart";
import type { CustomAnalyticsReportColumn } from "@redotech/redo-analytics-common/reports/custom-analytics-report";
import { getVisualizationColors } from "@redotech/redo-web/theme/colors";
import { downloadBlob } from "@redotech/web-util/download";

export const LINE_LABEL_STEP_MS = 86_400_000;

export const MAX_LINE_POINT_MARKERS = 31;

export function buildBarChartSeries(
  series: readonly CustomAnalyticsChartSeries[],
) {
  return series.map((s) => ({
    name: s.label,
    data: s.data.map((value) => value ?? 0),
  }));
}

export function buildLineChartSeries(
  series: readonly CustomAnalyticsChartSeries[],
) {
  return series.map((s) => ({
    name: s.label,
    data: s.data.map((value, index) => ({
      date: Temporal.Instant.fromEpochMilliseconds(index * LINE_LABEL_STEP_MS),
      value: value ?? 0,
    })),
  }));
}

export function buildLineChartDateFormatter(
  labels: readonly string[],
): (date: Temporal.Instant) => string {
  return (date: Temporal.Instant) =>
    labels[Math.round(date.epochMilliseconds / LINE_LABEL_STEP_MS)] ?? "";
}

/**
 * Synthetic report column fed to the existing custom report value formatter,
 * so the card reuses currency/percent/number rendering instead of
 * reimplementing it. The chart payload only carries kind + format; the other
 * column fields are required by the type but unused by the formatter.
 */
export function buildChartValueColumn(
  valueFormat: CustomAnalyticsChartValueFormat,
  label: string,
): CustomAnalyticsReportColumn {
  const base = {
    key: "value",
    sourceName: "VALUE",
    label,
    sqlType: "NUMBER",
    order: 0,
    align: "left" as const,
  };
  if (valueFormat.kind === "number" || valueFormat.kind === "percent") {
    return { ...base, kind: valueFormat.kind, format: valueFormat.format };
  }
  return { ...base, kind: valueFormat.kind };
}

export function buildTruncationNote(
  truncation: CustomAnalyticsChartTruncation | null,
): string | null {
  if (!truncation) {
    return null;
  }
  const notes: string[] = [];
  if (truncation.droppedRowCount > 0) {
    notes.push(
      `Showing the top results — ${truncation.droppedRowCount} ${
        truncation.droppedRowCount === 1 ? "row was" : "rows were"
      } left out.`,
    );
  }
  if (truncation.hasOtherBucket) {
    notes.push('Smaller values are grouped into "Other."');
  }
  return notes.length > 0 ? notes.join(" ") : null;
}

const PDF_PAGE_WIDTH = 612;
const PDF_PAGE_HEIGHT = 792;
const PDF_MARGIN = 48;

/**
 * Chart.js canvases are transparent; composite onto white so the standalone
 * image stays legible in dark-mode viewers and on non-white surfaces.
 */
export function snapshotCanvasOnWhite(
  canvas: HTMLCanvasElement,
): HTMLCanvasElement {
  const snapshot = document.createElement("canvas");
  snapshot.width = canvas.width;
  snapshot.height = canvas.height;
  const context = snapshot.getContext("2d");
  if (!context) {
    return canvas;
  }
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, snapshot.width, snapshot.height);
  context.drawImage(canvas, 0, 0);
  return snapshot;
}

export async function exportChartAsPng({
  chart,
  canvas,
}: {
  chart: CustomAnalyticsChart;
  canvas: HTMLCanvasElement;
}): Promise<void> {
  const snapshot = snapshotCanvasOnWhite(canvas);
  const blob = await new Promise<Blob>((resolve, reject) => {
    snapshot.toBlob(
      (result) =>
        result
          ? resolve(result)
          : reject(new Error("Chart image could not be captured.")),
      "image/png",
    );
  });
  await downloadBlob(blob, `${chartFilename(chart.title)}.png`);
}

/** Single-page PDF: title, date range, chart image, text legend, footnote. */
export async function exportChartAsPdf({
  chart,
  canvas,
  formatValue,
  truncationNote,
}: {
  chart: CustomAnalyticsChart;
  canvas: HTMLCanvasElement;
  formatValue: (value: number) => string;
  truncationNote: string | null;
}): Promise<void> {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");

  const pdfDocument = await PDFDocument.create();
  const page = pdfDocument.addPage([PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT]);
  const boldFont = await pdfDocument.embedFont(StandardFonts.HelveticaBold);
  const regularFont = await pdfDocument.embedFont(StandardFonts.Helvetica);
  // PDF drawing cannot consume CSS custom properties, so these mirror the
  // design system's primary/tertiary text colors as literals (same pattern
  // as the canvas chart palettes elsewhere in the app).
  const titleColor = rgb(0.06, 0.09, 0.13);
  const mutedColor = rgb(0.42, 0.45, 0.49);
  const contentWidth = PDF_PAGE_WIDTH - 2 * PDF_MARGIN;

  let cursorY = PDF_PAGE_HEIGHT - PDF_MARGIN;

  const drawLine = (
    text: string,
    font: typeof boldFont,
    size: number,
    color: ReturnType<typeof rgb>,
  ) => {
    cursorY -= size;
    page.drawText(truncateToWidth(text, font, size, contentWidth), {
      x: PDF_MARGIN,
      y: cursorY,
      size,
      font,
      color,
    });
    cursorY -= size * 0.5;
  };

  drawLine(chart.title, boldFont, 16, titleColor);
  if (chart.dateRangeLabel) {
    drawLine(chart.dateRangeLabel, regularFont, 11, mutedColor);
  }
  cursorY -= 12;

  const image = await pdfDocument.embedPng(canvas.toDataURL("image/png"));
  const imageScale = Math.min(
    contentWidth / image.width,
    (cursorY - PDF_MARGIN - 120) / image.height,
    1,
  );
  const imageWidth = image.width * imageScale;
  const imageHeight = image.height * imageScale;
  cursorY -= imageHeight;
  page.drawImage(image, {
    x: PDF_MARGIN + (contentWidth - imageWidth) / 2,
    y: cursorY,
    width: imageWidth,
    height: imageHeight,
  });
  cursorY -= 16;

  if (chart.chartType === "pie") {
    const data = chart.series[0]?.data ?? [];
    for (const [index, label] of chart.labels.entries()) {
      if (cursorY <= PDF_MARGIN + 24) {
        break;
      }
      const value = data[index];
      drawLine(
        `${label}: ${value == null ? "—" : formatValue(value)}`,
        regularFont,
        10,
        titleColor,
      );
    }
    cursorY -= 8;
  }

  if (
    (chart.chartType === "bar" || chart.chartType === "line") &&
    chart.series.length > 1
  ) {
    const legendFontSize = 9;
    const swatchSize = legendFontSize;
    const seriesColors = getVisualizationColors(chart.series.length);
    for (const [index, series] of chart.series.entries()) {
      if (cursorY <= PDF_MARGIN + swatchSize + 4) {
        break;
      }
      const hexColor = (seriesColors[index] ?? "#888888").replace("#", "");
      const r = parseInt(hexColor.slice(0, 2), 16) / 255;
      const g = parseInt(hexColor.slice(2, 4), 16) / 255;
      const b = parseInt(hexColor.slice(4, 6), 16) / 255;
      cursorY -= swatchSize + 3;
      page.drawRectangle({
        x: PDF_MARGIN,
        y: cursorY,
        width: swatchSize,
        height: swatchSize,
        color: rgb(r, g, b),
      });
      page.drawText(
        truncateToWidth(
          series.label,
          regularFont,
          legendFontSize,
          contentWidth - swatchSize - 6,
        ),
        {
          x: PDF_MARGIN + swatchSize + 6,
          y: cursorY,
          size: legendFontSize,
          font: regularFont,
          color: titleColor,
        },
      );
    }
    cursorY -= 8;
  }

  if (truncationNote && cursorY > PDF_MARGIN) {
    drawLine(truncationNote, regularFont, 9, mutedColor);
  }

  const bytes = await pdfDocument.save();
  await downloadBlob(
    new Blob([new Uint8Array(bytes)], { type: "application/pdf" }),
    `${chartFilename(chart.title)}.pdf`,
  );
}

// PDF standard fonts only cover WinAnsi. Replace anything outside Latin-1
// plus common WinAnsi punctuation/currency so widthOfTextAtSize/drawText
// don't throw on exotic label characters.
const NON_WIN_ANSI_PATTERN =
  /[^ -~\u{a0}-\u{ff}\u{2013}\u{2014}\u{2018}\u{2019}\u{201c}\u{201d}\u{2026}\u{20ac}]/gu;

export function truncateToWidth(
  text: string,
  font: { widthOfTextAtSize: (text: string, size: number) => number },
  size: number,
  maxWidth: number,
): string {
  let safe = text.replace(NON_WIN_ANSI_PATTERN, "?");
  if (font.widthOfTextAtSize(safe, size) <= maxWidth) {
    return safe;
  }
  while (
    safe.length > 1 &&
    font.widthOfTextAtSize(`${safe}…`, size) > maxWidth
  ) {
    safe = safe.slice(0, -1);
  }
  return `${safe}…`;
}

export function chartFilename(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/gu, "-")
      .replace(/^-+|-+$/gu, "") || "chart"
  );
}
